import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useProcessTargetDataTable } from 'generated/resources/datatable.graphql'
import { PerformanceTargetManagementDocument } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import DataTable from 'modules/core/components/DataTable'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { TargetAvatar } from 'modules/performance_process/components/common/TargetAvatar'

import { Actions } from '../Actions'
import { AddTargetsModal } from '../TargetModals/AddTargetsModal'

interface Props {
  reviewActive: boolean
}

const TargetManagementDataTable = ({ reviewActive }: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [showAddTargetsModal, toggleAddTargetsModal] = useToggle()
  const { reviewId } = useParams()
  const { data } = useQuery(PerformanceTargetManagementDocument, {
    variables: {
      id: Number(reviewId),
    },
  })

  const targetsAccessIds = data?.performance.reviewProcessTargets
  const alreadySelectedTargetIds = (targetsAccessIds || []).map((item) =>
    Number(item.id.split('-')[1])
  )

  const source = useProcessTargetDataTable(
    [
      {
        identifier: 'target',
        title: i18n.t('performance_process.reviews.peer_management.peers_datatable.employee'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ access }}',
        colSpan: 2,
        render: ({ access }) => access && <TargetAvatar access={access} />,
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        render: ({ id, access }) => {
          if (reviewActive) {
            return access?.employeeName && <Actions targetAccess={access} processTargetId={id} />
          } else {
            return null
          }
        },
      },
    ],
    {
      variables: {
        performanceReviewProcessIds: [Number(reviewId)],
        searchValue: debouncedSearch || '',
      },
    }
  )

  return (
    <>
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 20,
        }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableDefaultActions
            search={{
              placeholder: i18n.t('performance_process.reviews.target_management_search'),
              value: search,
              onChange: onSearch,
            }}
            actions={
              reviewActive && (
                <Button type="secondary" onClick={toggleAddTargetsModal}>
                  {i18n.t('performance_process.reviews.target_management_addition')}
                </Button>
              )
            }
          />
          <DataTable.Table />
        </Box>
      </DataTable.Provider>
      {showAddTargetsModal && (
        <AddTargetsModal
          onClose={toggleAddTargetsModal}
          alreadySelectedTargetIds={alreadySelectedTargetIds}
          alreadySelectedTargets={
            data?.performance?.reviewProcessTargets
              ?.map((target) => target.access?.employeeName)
              .filter(Boolean) || []
          }
        />
      )}
    </>
  )
}

export default TargetManagementDataTable
